import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const Chat = ({ token, otherUser, onBack }) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [zoomImage, setZoomImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const pollInterval = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const fetchMessages = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await axios.get(`/speeddate/api/messages?token=${token.toUpperCase()}&otherUserId=${otherUser.userId}`);
            setMessages(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() && !selectedImage) return;

        const content = newMessage.trim();
        const imageUrl = selectedImage;
        
        setNewMessage('');
        setSelectedImage(null);

        try {
            await axios.post('/speeddate/api/messages', {
                token: token.toUpperCase(),
                otherUserId: otherUser.userId,
                content,
                imageUrl
            });
            fetchMessages(true);
        } catch (err) {
            window.Swal.fire({ title: 'Errore', text: err.response?.data?.error || 'Impossibile inviare il messaggio', icon: 'error' });
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            window.Swal.fire('Errore', 'La foto deve essere inferiore a 5MB', 'error');
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
            setSelectedImage(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleBlock = () => {
        window.Swal.fire({
            title: 'Vuoi bloccare questo utente?',
            text: "Non potrai più ricevere o inviare messaggi a questa persona e sparirà dai tuoi match.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#db2777',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sì, blocca',
            cancelButtonText: 'Annulla'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.post('/speeddate/api/users/block', {
                        token: token.toUpperCase(),
                        targetUserId: otherUser.userId
                    });
                    window.Swal.fire('Bloccato!', 'L\'utente è stato bloccato con successo.', 'success');
                    onBack(); // Torna indietro alla lista
                } catch (err) {
                    window.Swal.fire('Errore', 'Impossibile bloccare l\'utente.', 'error');
                }
            }
        });
    };

    useEffect(() => {
        fetchMessages();
        pollInterval.current = setInterval(() => fetchMessages(true), 5000);
        return () => clearInterval(pollInterval.current);
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    return (
        <div className="mx-auto w-100 fade-in h-100 d-flex flex-column" style={{ maxWidth: '800px', minHeight: '80vh' }}>
            <div className="card glass-premium border-0 flex-grow-1 shadow-lg mx-2 d-flex flex-column" style={{ borderRadius: '45px', overflow: 'hidden' }}>
                {/* Header */}
                <div className="p-4 border-bottom d-flex align-items-center gap-3 bg-white bg-opacity-50">
                    <button className="btn btn-white shadow-sm rounded-circle d-flex align-items-center justify-content-center border-0" onClick={onBack} style={{ width: '40px', height: '40px' }}>
                        <i className="fas fa-arrow-left text-pink-600"></i>
                    </button>
                    <div className="d-flex align-items-center gap-3">
                        <div className="rounded-circle shadow-sm overflow-hidden border border-white" style={{ width: '45px', height: '45px' }}>
                            {otherUser.photo ? (
                                <img src={otherUser.photo} alt={otherUser.firstName} className="w-100 h-100 object-fit-cover" />
                            ) : (
                                <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-light">
                                    <i className="fas fa-user text-pink-600"></i>
                                </div>
                            )}
                        </div>
                        <div>
                            <h5 className="fw-900 text-dark mb-0">{otherUser.firstName}</h5>
                            <small className="text-muted fw-600">Messaggi Privati</small>
                        </div>
                    </div>
                    <div className="ms-auto d-flex gap-2">
                        {otherUser.instagram && (
                            <a href={`https://instagram.com/${otherUser.instagram.replace('@','')}`} target="_blank" rel="noreferrer" className="btn btn-sm btn-white rounded-circle shadow-sm border-0 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }} title="Instagram">
                                <i className="fab fa-instagram text-pink-600"></i>
                            </a>
                        )}
                        {otherUser.facebook && (
                            <a href={otherUser.facebook} target="_blank" rel="noreferrer" className="btn btn-sm btn-white rounded-circle shadow-sm border-0 d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }} title="Facebook">
                                <i className="fab fa-facebook-f text-pink-600"></i>
                            </a>
                        )}
                        <button className="btn btn-sm btn-light rounded-circle shadow-sm border-0 d-flex align-items-center justify-content-center" onClick={handleBlock} title="Blocca Contatto" style={{ width: '32px', height: '32px' }}>
                            <i className="fas fa-ban text-danger"></i>
                        </button>
                    </div>
                </div>

                {/* Messages Area */}
                <div className="flex-grow-1 p-4 overflow-auto d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.3)', maxHeight: '60vh' }}>
                    {loading && messages.length === 0 ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-pink-600" role="status"></div>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="text-center py-5 opacity-50">
                            <i className="fas fa-comments fa-3x mb-3 text-pink-600"></i>
                            <p className="fw-600">Inizia la conversazione!</p>
                        </div>
                    ) : (
                        messages.map((m, idx) => {
                            const { isMe } = m;
                            return (
                                <div key={idx} className={`d-flex ${isMe ? 'justify-content-end' : 'justify-content-start'}`}>
                                    <div className={`p-3 rounded-4 shadow-sm fw-600`} 
                                         style={{ 
                                             maxWidth: '80%', 
                                             background: isMe ? 'linear-gradient(45deg, #db2777, #f43f5e)' : 'white',
                                             color: isMe ? 'white' : 'black',
                                             borderRadius: isMe ? '20px 20px 0 20px' : '20px 20px 20px 0'
                                         }}>
                                        {m.imageUrl && (
                                            <div className="mb-2">
                                                <img 
                                                    src={m.imageUrl} 
                                                    alt="Allegato" 
                                                    className="img-fluid rounded-3 shadow-sm" 
                                                    style={{ maxHeight: '200px', objectFit: 'cover', cursor: 'pointer' }} 
                                                    onClick={() => setZoomImage(m.imageUrl)}
                                                />
                                            </div>
                                        )}
                                        {m.content && <div>{m.content}</div>}
                                        <div className={`text-end mt-1 ${isMe ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '0.6rem' }}>
                                            {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-4 bg-white bg-opacity-50 border-top position-relative">
                    {selectedImage && (
                        <div className="position-absolute bg-white p-2 rounded-3 shadow-sm border" style={{ bottom: '100%', left: '1.5rem', marginBottom: '0.5rem' }}>
                            <div className="position-relative">
                                <img src={selectedImage} alt="Preview" style={{ height: '80px', borderRadius: '8px', objectFit: 'cover' }} />
                                <button type="button" onClick={() => setSelectedImage(null)} className="btn btn-sm btn-danger rounded-circle position-absolute" style={{ top: '-10px', right: '-10px', width: '24px', height: '24px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <i className="fas fa-times" style={{ fontSize: '10px' }}></i>
                                </button>
                            </div>
                        </div>
                    )}
                    <form onSubmit={handleSendMessage} className="d-flex gap-2 align-items-center">
                        <input 
                            type="file" 
                            accept="image/*" 
                            ref={fileInputRef} 
                            style={{ display: 'none' }} 
                            onChange={handleImageChange} 
                        />
                        <input 
                            type="text" 
                            className="form-control rounded-pill border-0 shadow-sm px-4 py-3 fw-600" 
                            placeholder="Scrivi un messaggio..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                        />
                        <button type="button" onClick={() => fileInputRef.current?.click()} className="btn btn-light rounded-circle shadow-sm d-flex align-items-center justify-content-center border-0 text-secondary" style={{ width: '55px', height: '55px', flexShrink: 0 }} title="Allega foto">
                            <i className="fas fa-paperclip"></i>
                        </button>
                        <button type="submit" disabled={!newMessage.trim() && !selectedImage} className="btn btn-danger rounded-circle shadow-lg d-flex align-items-center justify-content-center border-0" style={{ width: '55px', height: '55px', flexShrink: 0, background: 'linear-gradient(45deg, #db2777, #f43f5e)', opacity: (!newMessage.trim() && !selectedImage) ? 0.5 : 1 }}>
                            <i className="fas fa-paper-plane"></i>
                        </button>
                    </form>
                </div>
            </div>

            {/* Zoom Image Modal */}
            {zoomImage && (
                <div 
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center fade-in" 
                    style={{ background: 'rgba(0,0,0,0.85)', zIndex: 9999, padding: '20px' }} 
                    onClick={() => setZoomImage(null)}
                >
                    <button className="btn btn-dark position-absolute rounded-circle shadow-lg" style={{ top: '20px', right: '20px', width: '45px', height: '45px', zIndex: 10000 }} onClick={() => setZoomImage(null)}>
                        <i className="fas fa-times"></i>
                    </button>
                    <img src={zoomImage} alt="Zoomed" style={{ maxWidth: '100%', maxHeight: '90%', objectFit: 'contain', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }} />
                </div>
            )}
        </div>
    );
};

export default Chat;
